/* eslint-disable no-unused-vars */
import PennyInput from "@components/Inputs/PennyInput"
import { zodResolver } from "@hookform/resolvers/zod"
import { functions, auth } from "@utils/firebase"
import type React from "react"
import { useState, useCallback, useEffect } from "react"
import { useHttpsCallable } from "react-firebase-hooks/functions"
import { type SubmitHandler, useForm } from "react-hook-form"
import { sendPasswordResetEmail } from "firebase/auth"
import { Link } from "react-router-dom"
import { twMerge } from "tailwind-merge"
import { z } from "zod"
import { ReactComponent as PennyLogo } from "../../assets/icons/penny_logo.svg"

type ForgotPassowrdProps = {}

const forgotPasswordSchema = z.object({
  email: z.string({ required_error: "Required" }).email({ message: "Must be a valid email" }),
})
type ForgotPasswordSchema = z.infer<typeof forgotPasswordSchema>

interface FormValues {
  email: string
  password: string
}

const ForgotPassword: React.FC<ForgotPassowrdProps> = () => {
  const [onSendView, setOnSendView] = useState(false)
  const [emailField, setEmailField] = useState("")

  return (
    <section className="w-full grow flex items-center justify-center">
      {!onSendView && (
        <ForgotPasswordSend setOnSendView={setOnSendView} setEmailField={setEmailField} emailField={emailField} />
      )}
      {onSendView && <EmailSent email={emailField} setOnSendView={setOnSendView} setEmailField={setEmailField} />}
    </section>
  )
}

interface EmailSentProps {
  email: string
  setOnSendView(bool: boolean): void
  setEmailField(str: string): void
}
const EmailSent: React.FC<EmailSentProps> = ({ email, setOnSendView, setEmailField }) => {
  return (
    <>
      <div className="max-w-full md:max-w-[400px] md:my-4 w-full py-10 mx-auto md:border md:border-pn_gray-200 bg-pn_paper-1 rounded-3xl">
        <div className="w-full py-4 px-6 flex items-center justify-center">
          <PennyLogo width={81} height={32} />
        </div>

        <div className="flex flex-col gap-y-4 py-4 px-6">
          <h2 className="text-center text-pn-large-title text-pn_gray-950">Email Sent</h2>
          <div className="text-pn-body-regular !font-normal text-center flex flex-col gap-y-6 text-pn_gray-600">
            <span>
              We've sent an e-mail with a link to reset your password to:{" "}
              <span className="text-pn-body-emphasis">{email}</span>
            </span>

            <span>Please follow the instructions in the e-mail in order to set a new password.</span>
          </div>
        </div>

        <div className="py-4 px-6 flex items-center justify-center">
          <Link to="/login" className="btn-secondary-lg w-full text-center">
            Back to Login
          </Link>
        </div>

        <div className="flex flex-col gap-y-6 py-4 px-6 text-center text-pn-callout-regular">
          <span>
            Didn't receive anything?{" "}
            <button
              className="underline"
              onClick={() => {
                setOnSendView(false)
                setEmailField(email)
              }}
            >
              Click here
            </button>{" "}
            to resend the password reset e-mail.
          </span>
          <span>If you have any other issues, please don't hesitate to contact us.</span>
        </div>
      </div>
    </>
  )
}

interface ForgotPasswordSendProps {
  setOnSendView(bool: boolean): void
  setEmailField(email: string): void
  emailField: string
}
const ForgotPasswordSend: React.FC<ForgotPasswordSendProps> = ({ setOnSendView, setEmailField, emailField }) => {
  const [sendPasswordResetEmailFirebase, sending] = useHttpsCallable(functions, "user-request_new_password")
  const [checkingUser, setCheckingUser] = useState(false)
  const [errorMessage, setErrorMessage] = useState<string | null>(null)
  const [retryAttempts, setRetryAttempts] = useState(0)

  const {
    formState: { errors },
    register,
    handleSubmit,
    watch,
  } = useForm<FormValues>({
    defaultValues: {
      email: emailField.length ? emailField : "",
    },
    resolver: zodResolver(forgotPasswordSchema),
  })

  // Clear error message when email changes
  const watchedEmail = watch("email")
  const [previousEmail, setPreviousEmail] = useState(emailField)
  
  useEffect(() => {
    if (watchedEmail !== previousEmail && errorMessage) {
      setErrorMessage(null)
    }
    setPreviousEmail(watchedEmail)
  }, [watchedEmail, errorMessage, previousEmail])

  // Debug: Log error message changes
  useEffect(() => {
    console.log("Error message state:", errorMessage)
  }, [errorMessage])

  interface PasswordResetResponse {
    success: boolean
    message?: string
  }

  const onSubmit: SubmitHandler<ForgotPasswordSchema> = useCallback(
    async (data) => {
      const { email } = data

      if (sending || checkingUser) return

      setErrorMessage(null)
      setCheckingUser(true)

      try {
        // Try using Firebase Auth directly first
        await sendPasswordResetEmail(auth, email)
        
        // If successful, user exists and email was sent
        setEmailField(email)
        setOnSendView(true)
      } catch (error: any) {
        console.error("Firebase Auth error:", error)
        console.error("Error code:", error.code)
        console.error("Error message:", error.message)
        
        // Ensure we always set an error message for the user
        let userErrorMessage = ""
        
        // Handle specific Firebase Auth error codes
        if (error.code === "auth/user-not-found") {
          userErrorMessage = "No account found with this email address."
        } else if (error.code === "auth/invalid-email") {
          userErrorMessage = "Please enter a valid email address."
        } else if (error.code === "auth/too-many-requests") {
          userErrorMessage = "Too many requests. Try again later."
        } else if (error.code === "auth/network-request-failed") {
          userErrorMessage = "Network error. Check your connection."
        } else {
          // For any other Firebase Auth error, try the fallback
          console.log("Attempting fallback with Firebase function...")
          
          try {
            const response = await sendPasswordResetEmailFirebase({
              email,
            })

            if (response) {
              const responseData = response.data as PasswordResetResponse

              if (responseData && responseData.success) {
                setEmailField(email)
                setOnSendView(true)
                return // Success, don't set any error message
              } else {
                userErrorMessage = responseData?.message || "Failed to send reset email."
              }
            } else {
              userErrorMessage = "No response received. Try again."
            }
          } catch (fallbackErr: any) {
            console.error("Fallback error:", fallbackErr)
            
            if (fallbackErr.code === "not-found") {
              userErrorMessage = "No account found with this email address."
            } else if (fallbackErr.code === "invalid-argument") {
              userErrorMessage = "Please enter a valid email address."
            } else if (fallbackErr.code === "deadline-exceeded") {
              userErrorMessage = "Request timed out. Try again later."
            } else {
              userErrorMessage = fallbackErr.message || "Failed to process request."
            }
          }
        }
        
        // Always set an error message if we reach here
        if (userErrorMessage) {
          setErrorMessage(userErrorMessage)
          setRetryAttempts((prev) => prev + 1)
        } else {
          setErrorMessage("An unexpected error occurred.")
          setRetryAttempts((prev) => prev + 1)
        }
      } finally {
        setCheckingUser(false)
      }
    },
    [sending, checkingUser, sendPasswordResetEmailFirebase, setEmailField, setOnSendView],
  )

  return (
    <>
      <div className="max-w-full md:max-w-[400px] md:my-4 w-full py-10 mx-auto md:border md:border-pn_gray-200 bg-pn_paper-1 rounded-3xl">
        <div className="w-full py-4 px-6 flex items-center justify-center">
          <PennyLogo width={81} height={32} />
        </div>

        <div className="flex flex-col gap-y-4 py-4 px-6">
          <h2 className="text-center text-pn-large-title text-pn_gray-950">Forgot Password?</h2>
          <span className="text-center text-pn-body-regular !font-normal text-pn_gray-600">
            Enter the e-mail address for your Penny account and we'll send you an e-mail with a link to reset your
            password.
          </span>
        </div>

        <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-y-4 py-4 px-6">
          <div className="flex flex-col gap-y-2">
            <PennyInput
              disabled={sending || checkingUser}
              {...register("email")}
              type="email"
              label="E-mail"
              id="email"
              name="email"
              placeholder="E-mail"
              error={!!errors.email?.message || !!errorMessage}
            />
            {(errors.email?.message || errorMessage) && (
              <span className="text-pn-callout-emphasis text-pn_red-500">
                {errors.email?.message || errorMessage}
              </span>
            )}
          </div>

          <button disabled={sending || checkingUser} type="submit" className="btn-primary-lg relative">
            {(sending || checkingUser) ? (
              <>
                <span className="opacity-0">Reset Password</span>
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                </div>
              </>
            ) : (
              "Reset Password"
            )}
          </button>

          {retryAttempts > 1 && (
            <div className="text-center mt-2">
              <span className="text-pn-callout-regular text-pn_gray-600">
                Having trouble?{" "}
                <Link to="https://www.thisispenny.com/contact" className="text-pn-callout-link">
                  Contact support
                </Link>
              </span>
            </div>
          )}
        </form>

        <div className="pt-4 pb-6 px-6 flex items-center justify-center">
          <span className="text-pn-callout-regular text-center">
            Already have a Penny account?{" "}
            <Link className="text-pn-callout-link" to="/login">
              Sign in
            </Link>
          </span>
        </div>

        <div className="py-4 px-6 flex items-center justify-center">
          <span className="text-pn-callout-regular text-center">
            By proceeding, you agree to the{" "}
            <Link to="https://www.thisispenny.com/terms-of-business" className="text-pn-callout-link">
              Terms and Conditions
            </Link>{" "}
            and{" "}
            <Link to="https://www.thisispenny.com/privacy-policy" className="text-pn-callout-link">
              Privacy Policy
            </Link>
          </span>
        </div>
      </div>
    </>
  )
}

export default ForgotPassword
